import React from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Hourglass, Lock, Mail, ExternalLink, Calendar, AlertTriangle, Users, Package } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { gymApi } from '@/lib/api';
import { Progress } from '@/components/ui/progress';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

export function TrialOverlay() {
    const { gym, user, logout } = useAuth();

    const isTrial = !!gym?.is_trial;
    const isExpired = gym?.status === 'expired' || (gym?.expiry_date && new Date(gym.expiry_date) < new Date());

    // Fetch stats for limits display
    const { data: stats } = useQuery({
        queryKey: ['trialStats', gym?.gym_id],
        queryFn: async () => {
            if (!gym?.gym_id) return { clientsCount: 0, inventoryCount: 0 };
            const [clientsRes, inventoryRes] = await Promise.all([
                gymApi.listClients(null, gym.gym_id),
                gymApi.listInventory(null, gym.gym_id)
            ]);
            return {
                clientsCount: clientsRes.success ? clientsRes.clients.length : 0,
                inventoryCount: inventoryRes.success ? inventoryRes.items.length : 0
            };
        },
        enabled: !!gym && user?.role !== 'super_admin' && (isTrial || isExpired),
    });

    if (!gym || user?.role === 'super_admin') return null;

    const isPending = gym.status === 'pending';
    const isInactive = gym.status === 'inactive';

    if (!isPending && !isExpired && !isInactive) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-slate-900/40 backdrop-blur-md">
            <Card className="w-full max-w-lg border-none shadow-2xl rounded-3xl overflow-hidden animate-in zoom-in duration-300 dark:bg-slate-900/95 dark:backdrop-blur-xl">
                <div className="h-2 bg-orange-600" />
                <CardHeader className="text-center pt-8">
                    <div className="mx-auto w-20 h-20 rounded-2xl bg-orange-50 flex items-center justify-center mb-6">
                        {isPending ? (
                            <Hourglass className="h-10 w-10 text-orange-600 animate-pulse" />
                        ) : isExpired ? (
                            <Calendar className="h-10 w-10 text-orange-600" />
                        ) : (
                            <Lock className="h-10 w-10 text-orange-600" />
                        )}
                    </div>
                    <CardTitle className="text-3xl font-black text-slate-900 dark:text-white leading-tight">
                        {isPending ? 'Registro en Revisión' : isExpired ? 'Prueba Finalizada' : 'Gimnasio Inactivo'}
                    </CardTitle>
                    <CardDescription className="text-lg font-medium text-slate-500 dark:text-slate-400 mt-2 px-6">
                        {isPending
                            ? 'Estamos preparando tu espacio. Un administrador del sistema aprobará tu solicitud en breve.'
                            : isExpired
                                ? 'Los 30 días de prueba han terminado. Para continuar usando GymPro SV, adquiere un plan.'
                                : 'Tu acceso ha sido restringido. Por favor contacta a soporte.'}
                    </CardDescription>
                </CardHeader>

                <CardContent className="space-y-6 px-10 pb-10">
                    <div className="bg-slate-50 dark:bg-slate-800/40 rounded-2xl p-6 border border-slate-100 dark:border-slate-800/50 flex flex-col gap-4">
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-slate-400 dark:text-slate-500 font-bold uppercase tracking-wider">Gimnasio</span>
                            <span className="text-slate-900 dark:text-white font-bold">{gym.name}</span>
                        </div>
                        {gym.expiry_date && (
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-slate-400 dark:text-slate-500 font-bold uppercase tracking-wider">Fecha de Expiración</span>
                                <span className="text-slate-900 dark:text-white font-bold">
                                    {format(new Date(gym.expiry_date), "d 'de' MMMM, yyyy", { locale: es })}
                                </span>
                            </div>
                        )}
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-slate-400 dark:text-slate-500 font-bold uppercase tracking-wider">Estado</span>
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase ${isPending ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                                }`}>
                                {isPending ? 'Pendiente' : isExpired ? 'Expirado' : 'Inactivo'}
                            </span>
                        </div>
                    </div>

                    {isExpired && stats && (
                        <div className="space-y-4 py-2 border-t border-slate-100 pt-6">
                            <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                                <AlertTriangle className="h-3 w-3" /> Uso Alcanzado en Prueba
                            </h4>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <div className="flex justify-between items-end">
                                        <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 flex items-center gap-1">
                                            <Users className="h-3 w-3" /> Socios
                                        </span>
                                        <span className="text-xs font-black text-slate-900 dark:text-white">{stats.clientsCount}/10</span>
                                    </div>
                                    <Progress value={(stats.clientsCount / 10) * 100} className="h-1.5" />
                                </div>
                                <div className="space-y-2">
                                    <div className="flex justify-between items-end">
                                        <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 flex items-center gap-1">
                                            <Package className="h-3 w-3" /> Inventario
                                        </span>
                                        <span className="text-xs font-black text-slate-900 dark:text-white">{stats.inventoryCount}/20</span>
                                    </div>
                                    <Progress value={(stats.inventoryCount / 20) * 100} className="h-1.5" />
                                </div>
                            </div>
                            <p className="text-[10px] text-slate-400 leading-relaxed italic">
                                * Al adquirir un plan se eliminan todos los límites de registros.
                            </p>
                        </div>
                    )}

                    <div className="flex flex-col gap-3">
                        {isExpired && (
                            <Button
                                className="h-14 w-full text-lg font-bold bg-orange-600 hover:bg-orange-700 text-white rounded-2xl shadow-xl shadow-orange-600/20"
                                onClick={() => window.open('https://wa.me/50379557231?text=Hola,%20me%20gustaría%20adquirir%20un%20plan%20para%20mi%20gimnasio%20en%20GymPro%20SV', '_blank')}
                            >
                                ADQUIRIR PLAN
                                <ExternalLink className="ml-2 h-5 w-5" />
                            </Button>
                        )}
                        <Button
                            variant="outline"
                            className="h-12 w-full text-slate-600 dark:text-slate-300 font-bold border-slate-200 dark:border-slate-800 rounded-xl"
                            onClick={logout}
                        >
                            CERRAR SESIÓN
                        </Button>
                    </div>

                    <p className="text-center text-xs text-slate-400 font-medium">
                        ¿Necesitas ayuda? <a href="mailto:soporte@gymprosv.com" className="text-orange-600 font-bold hover:underline">Contáctanos</a>
                    </p>
                </CardContent>
            </Card>
        </div>
    );
}
